import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function TwoFactorPage() {
  const { state } = useLocation();
  const { verify2fa } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!state?.challenge_token) {
    navigate("/login");
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (code.length < 6) return toast.error("Enter the 6-digit code");
    setSubmitting(true);
    try {
      const user = await verify2fa(state.challenge_token, code, remember);
      if (user.must_change_password) navigate("/change-password");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg grid place-items-center p-6">
      <Card className="w-full max-w-md border-border shadow-none">
        <CardContent className="p-8">
          <div className="tiny-label">Two-factor authentication</div>
          <h2 className="font-display text-2xl font-bold mt-2">Verify your identity</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {state.mfa_method === "email"
              ? "We sent a 6-digit code to your email. It expires in 10 minutes."
              : "Enter the 6-digit code from your authenticator app."}
          </p>

          <form className="mt-8 space-y-5" onSubmit={onSubmit}>
            <div>
              <Label>Verification code</Label>
              <div className="mt-2 flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="2fa-code-input">
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="2fa-remember-device" />
              <span>Remember this device for 90 days</span>
            </label>
            <Button type="submit" disabled={submitting || code.length < 6} className="w-full h-11" data-testid="2fa-submit-button">
              {submitting ? "Verifying…" : "Verify & continue"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/login")} className="w-full" data-testid="2fa-back-button">
              Back to sign in
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
